import { Injectable } from '@angular/core';
import { RestService } from './rest.service';

export interface MedicationEntry {
  name: string;
  dose: string;
  frequency: string;
  frequencyCustom?: string;
  startDate: string;
  approximateStart: string;
  consistency: string;
  sideEffects: string;
  reason: string;
}

/** Each flag is true, false, or null. null = never asked, and it is the default. */
export interface BleedingDetail {
  heavy: boolean | null;
  spotting: boolean | null;
  newOrUnexplained: boolean | null;
  /** Asked instead of the three above once she reports her periods stopped. */
  postAny: boolean | null;
  postHeavy: boolean | null;
}

/** One symptom she rated on this scan, 1-10. */
export interface RatedSymptom {
  key: string;
  value: number;
}

/**
 * Whether she reported a period in the last 12 months on the mapping's first
 * questionnaire. null means we do not know - she skipped it, chose "prefer not
 * to say", or the scan predates the question - and nothing may be assumed from
 * it, least of all a "no".
 */
export type PeriodLast12 = 'yes' | 'no' | null;

/** What the prep form reads off the scan itself rather than off a saved report. */
export interface RatedScanAnswers {
  rated: RatedSymptom[];
  periodLast12: PeriodLast12;
}

/**
 * Hormone treatment she tried before this one. `tried` is the same three states
 * as the bleeding flags: true, false, or null for never asked. The two details
 * only exist while `tried` is true.
 */
export interface PriorHormoneTherapy {
  tried: boolean | null;
  /** 'pills' | 'hormonal' | 'other'. She may have tried more than one. */
  types: string[];
  stopReason: string;
}

/** A condition she knows about and deliberately does not medicate. */
export interface UntreatedCondition {
  has: boolean | null;
  detail: string;
}

/**
 * Her everyday pattern, for tracking only. null everywhere until she answers,
 * so an unanswered question never reads as a reported behaviour.
 *
 * Asked as FREQUENCY, never as identity ("how many of your meals a week are
 * prepared at home", not "do you cook or order in"). Nothing here is scored or
 * labelled healthy versus unhealthy.
 */
export interface Lifestyle {
  alcohol: string | null;
  caffeine: string | null;
  exerciseTypes: string[];
  exerciseOther: string;
  exerciseWeekly: string | null;
  exerciseChange: string | null;
  exerciseNote: string;
  sleepHours: string | null;
  sleepWaking: string | null;
  sleepChange: string | null;
  sleepNote: string;
  stress: string | null;
  smoking: string | null;
  smokingQuitYear: string;
  mealsHome: string | null;
  hotMeals: string | null;
}

/** The same answers as the short clinician lines the report reads. */
export interface LifestyleSummary {
  alcohol?: string;
  caffeine?: string;
  exercise?: string;
  sleep?: string;
  stress?: string;
  smoking?: string;
  meals?: string;
}

export interface DoctorReportData {
  id?: number;
  scanId?: string;
  reportPurpose?: string;
  treatmentStatus?: string;
  medications: MedicationEntry[];
  priorHormoneTherapy?: PriorHormoneTherapy | null;
  untreatedCondition?: UntreatedCondition | null;
  bleedingPatterns: string[];
  bleedingDetail?: BleedingDetail | null;
  bleedingNotes: string;
  lifestyle?: Lifestyle | null;
  doctorNotes?: string;
  visitGoal?: string;
  /** The three she most wants to improve, in her order, as symptom keys. */
  concerns?: string[];
}

export interface ReportSymptomEntry {
  key: string;
  value: number;
  note?: string;
  delta: number | null;
}

export interface ReportClusterEntry {
  key: string;
  impact: 'High' | 'Moderate' | 'Low';
  items: { key: string; value: number; delta: number | null }[];
}

export interface ReportSignalEntry {
  key: string;
  value: string;
  delta?: string;
  isPositive?: boolean;
  // Normalized 0-100 score computed server-side with the same formula as the
  // consumer THINK screen. When present, use it directly (single source of truth).
  score?: number;
  // Stage-B bonus domain (flexibility / attention) that wasn't administered for
  // this scan — the report still lists the row, marked "not measured".
  notMeasured?: boolean;
}

export interface ReportChangeEntry {
  key: string;
  delta: string;
}

export interface ReportTimelinePoint {
  scanNumber?: number;
  week: number;
  date: string;
  symptoms: Record<string, number>;
}

export interface ReportMedicationEvent {
  week: number;
  label: string;
  color: string;
}

/** The gynaecological procedure she reported, and the years around it. */
/** A side effect she reported, on the date of the mapping she wrote it on. */
export interface ReportSideEffectEvent {
  date: string;
  label: string;
  medication?: string;
}

export interface ReportSurgeryInfo {
  procedure?: string | null;
  ovaries_removed?: string | null;
  year?: number | null;
  hormone_therapy?: string | null;
  hormone_therapy_year?: number | null;
}

/**
 * What she marked on the mapping's first questionnaire. It lives on the scan
 * rather than on the report, so the server now carries it through with the rest
 * of her answers. Unanswered stays null or empty, never a denial.
 */
export interface ReportIntakeAnswers {
  periodLast12: 'yes' | 'no' | null;
  /** How long ago her last period was, in buckets, or null when not asked. */
  lastPeriod?: string | null;
  /** Whether she reports hormonal contraception. null when never answered. */
  contraception?: 'yes' | 'no' | null;
  contraceptionMethods: string[];
  noBleedReasons: string[];
  surgery: ReportSurgeryInfo | null;
  symptoms: string[];
  symptomFollowUps: Record<string, string>;
  other: string;
}

export interface ReportDomainSeries {
  key: string;
  series: (number | null)[];
}

export interface ReportDomainTrends {
  dates: string[];
  cognitive: ReportDomainSeries[];
  facial: ReportDomainSeries[];
}

export interface ReportViewData {
  patientName: string;
  patientAge: string;
  visitNumber: number;
  checkDate: string;
  scanCount?: number;
  symptomScanCount?: number;
  treatmentStatus: string;
  medications: MedicationEntry[];
  reportPurpose: string;
  visitFocusTags: string[];
  overallPattern: 'mixed' | 'improved' | 'worsened' | 'stable';
  improvements: string[];
  worsened: string[];
  stableAreas: string[];
  topSymptoms: ReportSymptomEntry[];
  symptomClusters: ReportClusterEntry[];
  changesImproved: ReportChangeEntry[];
  changesIncreased: ReportChangeEntry[];
  symptomTimeline?: ReportTimelinePoint[];
  hrtEvents?: ReportMedicationEvent[];
  sideEffectEvents?: ReportSideEffectEvent[];
  cognitiveSignals: ReportSignalEntry[];
  physiologicalSignals: ReportSignalEntry[];
  // The same measures read across the mappings, index-aligned to `dates`, for the
  // trends under the timeline. A mapping that did not measure one holds null.
  domainTrends?: ReportDomainTrends;
  // SENSE (vision + hearing) self-check. sense-2.0 records only, ascending, last =
  // reported scan. Change is computed client-side (report-view senseBlock getter).
  sense?: { scans: any[] };
  // What she tried before this treatment, and the condition she leaves untreated.
  // `tried` / `has` are null when the question was never put to her. The server
  // view model already carries both; this mirrors it so the payload is typed.
  priorHormoneTherapy?: PriorHormoneTherapy | null;
  untreatedCondition?: UntreatedCondition | null;
  bleedingPatterns: string[];
  bleedingDetail?: BleedingDetail | null;
  bleedingNotes: string;
  lifestyle?: Lifestyle | null;
  lifestyleSummary?: LifestyleSummary | null;
  doctorNotes: string;
  visitGoal: string;
  concerns?: string[];
  sideEffects: string[];
  // Safety self-report flags (safety spec 2026-08-17): shown at the top of the
  // report, never scored / never part of the symptom-load index.
  safetyFlags?: string[];
  // The rest of the first questionnaire, as she answered it. Optional so the
  // report fixtures and older payloads stay valid.
  intake?: ReportIntakeAnswers;
}

@Injectable({
  providedIn: 'root',
})
export class DoctorReportService {
  constructor(private restService: RestService) {}

  async getLatestReport(): Promise<DoctorReportData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: DoctorReportData | null;
      }>({
        method: 'GET',
        path: 'doctor-report/latest',
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to fetch latest report:', error);
      return null;
    }
  }

  async getReportByScan(scanId: string): Promise<DoctorReportData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: DoctorReportData | null;
      }>({
        method: 'GET',
        path: `doctor-report/by-scan/${scanId}`,
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to fetch report by scan:', error);
      return null;
    }
  }

  /**
   * Everything she rated above zero on this scan, highest first. Lighter than
   * the full view-data build -- the prep form only needs names and ratings.
   */
  async getRatedSymptoms(scanId: string): Promise<RatedScanAnswers> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: RatedSymptom[];
        periodLast12: PeriodLast12;
      }>({
        method: 'GET',
        path: `doctor-report/${scanId}/rated-symptoms`,
        throwError: true,
      });
      return {
        rated: response?.data || [],
        periodLast12: response?.periodLast12 ?? null,
      };
    } catch (error) {
      console.error('DoctorReportService: failed to fetch rated symptoms:', error);
      return { rated: [], periodLast12: null };
    }
  }

  async getReport(id: number): Promise<DoctorReportData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: DoctorReportData;
      }>({
        method: 'GET',
        path: `doctor-report/${id}`,
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to fetch report:', error);
      return null;
    }
  }

  async getReportViewData(scanId: string): Promise<ReportViewData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: ReportViewData;
      }>({
        method: 'GET',
        path: `doctor-report/${scanId}/view-data`,
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to fetch report view data:', error);
      return null;
    }
  }

  /** The report behind a print token (the guard-free print route). */
  async getPrintViewData(token: string): Promise<ReportViewData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: ReportViewData;
      }>({
        method: 'GET',
        path: `doctor-report/print/${encodeURIComponent(token)}/view-data`,
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to fetch print view data:', error);
      return null;
    }
  }

  /**
   * A public, short-lived URL that answers with the report as a PDF. It is what
   * the print button falls back to in the browsers that never open a print
   * dialog (the installed app's WebView, Chrome / Edge / Firefox on iOS,
   * Samsung Internet): opening a URL always works there, printing does not.
   */
  async createPdfUrl(scanId: string): Promise<string> {
    const response = await this.restService.request<{
      message: string;
      data: { token: string };
    }>({
      method: 'GET',
      path: `doctor-report/${scanId}/pdf-link`,
      throwError: true,
    });
    const token = response?.data?.token;
    if (!token) throw new Error('No print token');
    const baseUrl = this.restService['baseUrl'];
    return (
      new URL(
        `doctor-report/print/${encodeURIComponent(token)}/pdf`,
        baseUrl
      ).toString() + `?clientUrl=${encodeURIComponent(window.location.href)}`
    );
  }

  async downloadPdf(scanId: string): Promise<Blob> {
    const token = this.restService['sessionService'].session?.token || '';
    const baseUrl = this.restService['baseUrl'];
    const clientUrl = window.location.href;
    const url = new URL(`doctor-report/${scanId}/pdf`, baseUrl).toString()
      + `?clientUrl=${encodeURIComponent(clientUrl)}`;

    const response = await fetch(url, {
      headers: { Authorization: token },
    });

    if (!response.ok) {
      throw new Error(`PDF generation failed: ${response.status}`);
    }

    return response.blob();
  }

  async saveReport(data: DoctorReportData): Promise<DoctorReportData | null> {
    try {
      const response = await this.restService.request<{
        message: string;
        data: DoctorReportData;
      }>({
        method: 'POST',
        path: 'doctor-report/save',
        body: data,
        throwError: true,
      });
      return response?.data || null;
    } catch (error) {
      console.error('DoctorReportService: failed to save report:', error);
      return null;
    }
  }
}
